/**
 * @file usePagePublishing.ts
 * @description Черновик и публикация открытой страницы в конструкторе
 * (phase19.16 Р3а; SB126, SB128; uiux-r3 §3.1; контракт docs/api/site-pages.md).
 *
 * Источник правды — сервер: «что изменено», отпечаток, кто правил — ответ
 * `GET pages/:id/changes`; он перечитывается после каждой правки страницы
 * (с задержкой — серия правок даёт один запрос) и после действий. Правки
 * других страниц и мест (`GET pages/changes`) нужны главному действию:
 * «изменено ещё что-то» → окно публикации; перечитываются и при уходе со
 * страницы на экран магазина (черновик места мог остаться).
 *
 * Действия шлют отпечаток, который видел человек; ждущее перечитывание
 * (таймер или запрос в пути) сначала доводится до конца — своя свежая правка
 * не должна давать 409. «Вернуть как было» из тоста привязан к странице,
 * которую публиковали (`revertTo(pageId, …)`), а не к открытой сейчас.
 * После «Отменить правки» и возврата версии без `keepDraft` блоки на
 * сервере другие — история «Отменить / Вернуть» сбрасывается.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import type { BuilderPagesApi } from "./useBuilderPages";
import {
  sitePagePublish,
  type PublishPagesResult,
  type SitePageChangesDto,
  type SitePageRollbackResult,
} from "../../lib/api-site-page-publish";
import { pagePublishErrorText } from "../../lib/site-builder/page-publish-errors";

/** Задержка перечитывания после правки (серия правок — один запрос). */
export const CHANGES_REFRESH_MS = 600;

/** Виды страниц со своей публикацией (шаблон товара публикуется с видом сайта, SB61). */
const PUBLISHABLE_KINDS: ReadonlySet<string> = new Set(["SYSTEM_HOME", "CUSTOM", "SLOT"]);

export interface PagePublishingApi {
  /** «Что изменено» открытой страницы; `null` — грузится, ошибка или публиковать нечего (шаблон, экран). */
  changes: SitePageChangesDto | null;
  error: string | null;
  /** Страницы и места с правками, кроме открытой. */
  others: SitePageChangesDto[];
  /** Перечитать сводку открытой страницы сейчас. */
  reload: () => Promise<SitePageChangesDto | null>;
  /** Перечитать правки других страниц. */
  reloadOthers: () => Promise<void>;
  /** Опубликовать открытую страницу; `previousLive` — версия, что была на сайте (для «Вернуть как было»). */
  publish: (options?: { show?: boolean; acknowledgeChecks?: boolean }) => Promise<{ result: PublishPagesResult; previousLive: number | null }>;
  /** «Отменить правки»: черновик := версия на сайте. */
  discard: () => Promise<void>;
  /** «Вернуть версию N» открытой страницы; правки черновика пропадают. */
  rollback: (version: number) => Promise<SitePageRollbackResult>;
  /** «Вернуть как было» из тоста: прежняя версия страницы `pageId`, черновик остаётся. */
  revertTo: (pageId: string, version: number) => Promise<SitePageRollbackResult>;
  /** Перечитать всё после действия: страницу, список, сводки, канву. */
  afterChange: () => Promise<void>;
}

export function usePagePublishing(pages: BuilderPagesApi, opts: { enabled: boolean; onDraftReplaced: () => void }): PagePublishingApi {
  const { page, reloadPage, reloadList, bumpCanvas } = pages;
  const pageId = opts.enabled && page && PUBLISHABLE_KINDS.has(page.kind) ? page.id : null;
  const [changes, setChanges] = useState<SitePageChangesDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [others, setOthers] = useState<SitePageChangesDto[]>([]);
  const currentId = useRef(pageId);
  currentId.current = pageId;
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Запрос сводки в пути: действие ждёт его, а не берёт прежний отпечаток.
  const inflight = useRef<Promise<SitePageChangesDto | null> | null>(null);
  const inflightSeq = useRef(0);
  const { onDraftReplaced } = opts;

  const reload = useCallback((): Promise<SitePageChangesDto | null> => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    const id = currentId.current;
    if (!id) return Promise.resolve(null);
    // Номер запроса: свой ли промис снимать из `inflight` по окончании.
    const token = ++inflightSeq.current;
    const run = (async (): Promise<SitePageChangesDto | null> => {
      try {
        const next = await sitePagePublish.changes(id);
        // Быстрая смена страниц: ответ по прежней не ложится на новую.
        if (currentId.current !== id) return null;
        setChanges(next);
        setError(null);
        return next;
      } catch (err) {
        if (currentId.current === id) setError(pagePublishErrorText(err, "Не удалось проверить черновик страницы. Сайт и черновик в порядке."));
        return null;
      } finally {
        if (inflightSeq.current === token) inflight.current = null;
      }
    })();
    inflight.current = run;
    return run;
  }, []);

  // Правки других страниц уже загружались (экран магазина после перезагрузки — ещё нет).
  const othersLoaded = useRef(false);
  const reloadOthers = useCallback(async () => {
    if (!opts.enabled) return;
    try {
      const list = await sitePagePublish.changesList();
      othersLoaded.current = true;
      setOthers(list.filter((c) => c.hasChanges));
    } catch {
      // Без списка главное действие решает по открытой странице и виду сайта.
      setOthers([]);
    }
  }, [opts.enabled]);

  // Сменилась страница — сводка заново, сразу; правка той же страницы — с задержкой.
  const lastId = useRef<string | null>(null);
  useEffect(() => {
    if (!pageId) {
      setChanges(null);
      setError(null);
      // Ушли со страницы (экран магазина, шаблон): её правки теперь — «другие страницы»;
      // открыли экран «Блог»/«Каталог» сразу (перезагрузка) — черновики мест надо загрузить (QA D7).
      if (lastId.current !== null || !othersLoaded.current) void reloadOthers();
      lastId.current = null;
      return;
    }
    if (lastId.current !== pageId) {
      lastId.current = pageId;
      setChanges(null);
      void reload();
      void reloadOthers();
      return;
    }
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => void reload(), CHANGES_REFRESH_MS);
  }, [page, pageId, reload, reloadOthers]);
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current);
  }, []);

  /** Сводка, которую видел человек; ждущее перечитывание — довести до конца. */
  const seen = useCallback(async (): Promise<SitePageChangesDto> => {
    let current: SitePageChangesDto | null = changes;
    if (timer.current || !changes) current = await reload();
    else if (inflight.current) current = await inflight.current;
    if (!current) throw new Error("Страница не загружена");
    return current;
  }, [changes, reload]);

  const afterChange = useCallback(async () => {
    await Promise.all([reloadPage(), reloadList(), reload(), reloadOthers()]);
    bumpCanvas();
  }, [bumpCanvas, reload, reloadList, reloadOthers, reloadPage]);

  const publish = useCallback(
    async (options: { show?: boolean; acknowledgeChecks?: boolean } = {}) => {
      const current = await seen();
      const result = await sitePagePublish.publish(current.pageId, { fingerprint: current.fingerprint, ...options });
      await afterChange();
      return { result, previousLive: current.live?.version ?? null };
    },
    [afterChange, seen],
  );

  const discard = useCallback(async () => {
    const current = await seen();
    await sitePagePublish.discard(current.pageId, current.fingerprint);
    onDraftReplaced();
    await afterChange();
  }, [afterChange, onDraftReplaced, seen]);

  const rollback = useCallback(
    async (version: number) => {
      const current = await seen();
      const result = await sitePagePublish.rollback(current.pageId, version, { fingerprint: current.fingerprint });
      onDraftReplaced();
      await afterChange();
      return result;
    },
    [afterChange, onDraftReplaced, seen],
  );

  const revertTo = useCallback(
    async (targetId: string, version: number) => {
      // Черновик не трогается — отпечаток берём свежий у той страницы, которую публиковали.
      const fresh = await sitePagePublish.changes(targetId);
      const result = await sitePagePublish.rollback(targetId, version, { fingerprint: fresh.fingerprint, keepDraft: true });
      await afterChange();
      return result;
    },
    [afterChange],
  );

  return {
    changes: pageId ? changes : null,
    error: pageId ? error : null,
    others: others.filter((o) => o.pageId !== pageId),
    reload,
    reloadOthers,
    publish,
    discard,
    rollback,
    revertTo,
    afterChange,
  };
}
